import { z } from 'zod';
import { take } from "rxjs/operators";
import { ChangeDetectionStrategy, Component, signal } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import { DragulaService } from "ng2-dragula";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { DragContainerComponent, ModalControlComponent } from "@medsurf/flat-components";
import { ZodSharedValidator } from "@medsurf/flat-validators";

@Component({
    selector: 'medsurf-slide-edit-image-poi-form',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './image-poi-form.component.html',
    styleUrls: ['./image-poi-form.component.scss'],
    standalone: false
})
export class ImagePoiFormComponent extends DragContainerComponent {
  #isSubmitted = signal(false);
  image: FlatModels.ImageEntityModels.Image | null;
  imagePoiOptionArray: {value: string, name: any}[];
  imagePoiForm = new FormGroup(
    {
      id: new FormControl<null | string>(null)
    },
    {
      validators: ZodSharedValidator.validate(
        z.object({
          id: z.union([z.string().uuid().nullable(), z.literal('all')])
        })
      ),
      updateOn: 'change'
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public dialogService: DialogService,
              public translate: TranslateService,
              dragulaService: DragulaService) {
    super(dragulaService);
    
    // Update Image
    this.slideAuthorFacade.currentSelectedSlideImage$.pipe(takeUntilDestroyed()).subscribe((image) => {
      this.image = structuredClone(image);
      if (this.image) {
        this.slideAuthorFacade.imagePoiOptionArray$(this.image, this.translate).pipe(take(1)).subscribe(imagePoiOptionArray => {
          this.imagePoiOptionArray = imagePoiOptionArray;
        });
      } else {
        this.imagePoiOptionArray = [];
      }
    });
  }
  
  onSubmit() {
    if (this.#isSubmitted() || this.imagePoiForm.invalid || !this.imagePoiForm.value.id || !this.image) {
      return;
    }
    this.#isSubmitted.set(true);
    
    if (this.imagePoiForm.value.id === 'all') {
      const pois = this.imagePoiOptionArray.reduce((arr, option) => {
        if (option.value !== 'all') {
          arr.push(option.value);
        }
        return arr;
      }, [] as string[]);
      // Request Add Pois To Image
      this.slideAuthorFacade.requestAddPoiIdsToImage(this.image, pois, 'history.pois_add_to_image').subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.imagePoiForm);
          this.#isSubmitted.set(false);
          this.imagePoiForm.reset();
        },
        next: () => {
          this.#isSubmitted.set(false);
          this.imagePoiForm.reset();
        }
      });
    } else {
      // Request Add Poi To Image
      this.slideAuthorFacade.requestAddPoiIdToImage(this.image, this.imagePoiForm.value.id, 'history.poi_add_to_image').subscribe({
        error: (result) => {
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.imagePoiForm);
          this.#isSubmitted.set(false);
          this.imagePoiForm.reset();
        },
        next: () => {
          this.#isSubmitted.set(false);
          this.imagePoiForm.reset();
        }
      });
    }
  }
  
  //<editor-fold desc="Drag And Drop">
  
  onImagePoiMoved(pois: FlatModels.PointOfInterestEntityModels.PointOfInterest[]) {
    if (!this.image) return;
    this.slideAuthorFacade.requestRearrangeImagePois(this.image, pois).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  //</editor-fold>
  
  trackByPoi(index: number,
             item: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    return item.id || index;
  }
  
  goToPoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    this.slideAuthorFacade.requestGoToPoi(poi).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }
  
  removePoiFromImage(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    if (!this.image) return;
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.image_poi_remove'),
      data: {
        label: 'global.image_poi_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (this.image && remove) {
        // Request Remove Poi From image
        this.slideAuthorFacade.requestRemovePoiFromImage(this.image, poi.id, 'history.poi_remove_from_image').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }
}
